import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { Client, UserRepository } from "@amityco/ts-sdk";
import { Dialog, ExpanderListDriver, Paginator, Progress, Title } from "@components";
import { Button, NotFoundState, TableSearch } from "@rio-cloud/rio-uikit";
import {
  setAvatarDriverSelected,
  setIdDriverSelected,
  setNameDriverSelected,
  setUniqueProfileIdTrip,
  useAppSelector
} from "@store";
import { Driver, NavigateState } from "@types";

import { Notification } from "@rio-cloud/rio-uikit";

import { sortDrivers } from "@utils";
import { ProgressOptions } from "src/components/Progress/Trip";
import { AuthContext } from "src/providers/AuthProvider";
import { fetchDriverListV1 } from "src/services/routes";
import { getFollowers } from "src/services/social";

const PAGE_SIZE = 10;

const TripsDriverScreen = () => {
  const dispatch = useDispatch();

  const { amityRioDrivers, all } = useContext(AuthContext)

  const { idDriverSelected, nameDriverSelected, avatarDriverSelected }: any = useAppSelector((state) => state.trips);
  const userIdAmity = useAppSelector((state) => state.login.userIdAmity);
  const [search] = useSearchParams();
  const location = useLocation();

  const [error, setError] = useState("");

  const [searchValue, setSearchValue] = useState("");
  const [selectedItems, setSelectedItems] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<Driver[]>([]);
  const [nameS, setName] = useState("");
  const [userId, setUserId] = useState("");
  const [avatar, setAvatar] = useState("");
  const [uniqueProfileId, setUniqueProfileId] = useState('');
  const [pages, setPages] = useState(1);

  const [userInfo, setUserInfo] = useState<Driver | undefined>();
  const [openPerfilModal, setPerfilModal] = useState(false);
  const [openVehicleModal, setVehicleModal] = useState(false);

  const [total, setTotal] = useState(0);
  const [allDrivers, setAllDrivers] = useState<Driver[]>([]);
  const [copyfiltered, setCopyFiltered] = useState<Driver[]>([]);

  const navigateState = location.state as NavigateState | null;
  const [userDetailAmity, setUserDetailAmity] = useState<Amity.User>();

  useEffect(() => {
    if (!userId && idDriverSelected) {
      setSelectedItems(idDriverSelected);
      setName(nameDriverSelected);
      setUserId(idDriverSelected);
      setAvatar(avatarDriverSelected);
    }
  }, [idDriverSelected, nameDriverSelected, avatarDriverSelected, userId])

  // Function to normalize text by removing accents and converting to lowercase
  const normalizeText = (text: string): string => {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  };

  const handleSearchName = (text: string): void => {
    setSearchValue(text);
    if (!text.length || text.length < 1) {
      setPage(1);
      setData(allDrivers.slice(0, PAGE_SIZE));
      setTotal(allDrivers.length);
      setPages(Math.ceil(allDrivers.length / PAGE_SIZE));
      return;
    }

    const normalizedSearchText = normalizeText(text);
    const filtered = allDrivers.filter((driver) => {
      const normalizedDriverName = normalizeText(driver?.name || '');
      return normalizedDriverName.includes(normalizedSearchText);
    });
    const ordered = sortDrivers(filtered, selectedItems);

    setTotal(ordered.length);
    setCopyFiltered(ordered);
    setPage(1);
    setData(ordered.slice(0, PAGE_SIZE));
    setPages(Math.ceil(ordered.length / PAGE_SIZE));
  };

  const initialData = async (): Promise<void> => {
    const [response, resFollowers] = await Promise.all([
      fetchDriverListV1(),
      getFollowers(userIdAmity),
    ]);


    const rioUsers: Driver[] = response.data.result.drivers.map((driver: Driver): Driver => {
      const id = driver.uniqueProfileId ? driver.uniqueProfileId : driver.userId;

      if (!id) {
        return { ...driver, isEnabled: false, type: 'MY_TEAM', origin: 'RIO' };
      }

      const metadataRioUser = amityRioDrivers?.[id]?.metadata || {};

      const name = metadataRioUser?.displayName
        ? metadataRioUser.displayName
        : metadataRioUser?.givenName && metadataRioUser?.familyName
          ? metadataRioUser.givenName + " " + metadataRioUser.familyName
          : driver.name;

      return {
        ...driver,
        userId: driver?.userId,
        uniqueProfileId: driver.uniqueProfileId,
        name: name,
        avatar: metadataRioUser?.imageProfile?.fileUrl || "",
        type: driver.profileType === "MANAGER" ? "MANAGER" : "MY_TEAM",
        origin: "RIO",
        isEnabled: !!amityRioDrivers?.[id]?.metadata,
      };
    });


    const amityUsers: Driver[] = resFollowers.data?.result?.users
      ?.filter((driver: Amity.User) =>
        (driver?.metadata?.profileType && driver?.metadata?.profileType !== "MANAGER") ||
        (driver?.metadata?.profileType === "MANAGER" && driver?.metadata?.cnhCategory)
      )
      .filter((driver: Amity.User) => !rioUsers?.some((userRio: Driver) => userRio.uniqueProfileId === driver.userId || userRio.userId === driver.userId))
      .map((user: Amity.User): Driver => {
        const metadata = user?.metadata;
        return {
          id: user.userId,
          name: metadata?.displayName || metadata?.givenName + " " + metadata?.familyName,
          userId: user.userId,
          avatar: metadata?.imageProfile?.fileUrl,
          uniqueProfileId: user.userId,
          type: "CONNECTED",
          origin: "AMITY",
          isEnabled: true,
        };
      });

    const totalArr: number = amityUsers.length + rioUsers.length;
    const grouped: Driver[] = [...amityUsers, ...rioUsers];

    const selectedId = grouped.find((d) => d.userId === idDriverSelected)?.id;
    const finalDrivers: Driver[] = sortDrivers(grouped, selectedId);

    setAllDrivers(finalDrivers);
    setPage(1);
    setData(finalDrivers.slice(0, PAGE_SIZE));
    setTotal(totalArr);
    setPages(Math.ceil(totalArr / PAGE_SIZE));
  };

  const modals = {
    CALENDAR: (driver: Driver) => {
      console.log(`open calendar modal ${driver.id}`);
    },
    PROFILE: (driver: Driver) => handlePerfilSocial(driver),
    VEHICLE: (driver: Driver) => {
      console.log(`vehicle modal open ${driver.id}`);
    },
  };

  const handlePerfilSocial = async (driver: Driver) => {
    try {
      setUserInfo(driver);
      const userDriverId = driver.uniqueProfileId ? driver.uniqueProfileId : driver.userId;
      const response = await UserRepository.getUserByIds([userDriverId!]);
      setUserDetailAmity(response?.data ? response.data[0] : undefined);

      setPerfilModal(true);
    } catch (error) {
      console.error(error)
    }

  };

  const handleClosePerfilModal = () => {
    setPerfilModal(false);
  };

  const handleCloseVehicleModal = () => {
    setVehicleModal(false);
  };

  // Atualização em handleChecked:
  const handleChecked = (
    checked: boolean,
    id: string,
    name: string,
    _userIdN?: string,
    avatar?: string,
    uniqueProfileIdN?: string,
  ): void => {
    // Se clicar no já selecionado, desseleciona
    if (selectedItems === id) {
      setSelectedItems("");
      setName("");
      setUserId("");
      setAvatar("");
      setUniqueProfileId("");

      const startIndex = (page - 1) * PAGE_SIZE;
      const endIndex = startIndex + PAGE_SIZE;
      setData(allDrivers.slice(startIndex, endIndex));
      return;
    }

    // Limpa o filtro de pesquisa ao selecionar
    setSearchValue("");
    setCopyFiltered([]);

    setTotal(allDrivers.length);
    setPages(Math.ceil(allDrivers.length / PAGE_SIZE));
    setSelectedItems(id);
    setName(name);
    setUserId(_userIdN as string); // ainda pode ser usado para submissão
    setAvatar(avatar || "");
    setUniqueProfileId(uniqueProfileIdN as string);

    const index = allDrivers.findIndex((d) => d.id === id);
    if (index !== -1) {
      const selectedDriver = allDrivers[index];
      const reordered: Driver[] = sortDrivers(allDrivers, selectedDriver.id);
      setAllDrivers(reordered);
      ManipulatorPage(reordered, page, selectedDriver.id, false);
      setCopyFiltered([]);
    }
  };

  const navigate = useNavigate();
  const currentParams = new URLSearchParams(search);

  const setDriver = () => {
    const find = all?.find((res: {userId: string}) => res?.userId === userId);
    const uuid = uniqueProfileId || find?.uniqueProfileId;
    
    dispatch(setIdDriverSelected(userId || ''));
    dispatch(setUniqueProfileIdTrip(uuid));
    dispatch(setNameDriverSelected(nameS));
    dispatch(setAvatarDriverSelected(avatar));
  }

  const handleSubmit = () => {
    setDriver()

    navigate('/routing/trips/vehicle?' + currentParams.toString(), {
      state: {
        edit: navigateState?.edit,
        csv: navigateState?.csv,
        csvData: navigateState?.csvData,
      }
    })
  };

  const handleCancel = () => {
    const url = currentParams.get('editTrip') ? '/routing/trips/update?' : '/routing/trips/schedule?';
    navigate(url + currentParams.toString(), {
      state: {
        edit: navigateState?.edit,
        csv: navigateState?.csv,
        csvData: navigateState?.csvData,
      }
    })
  };

  const ManipulatorPage = (list: Driver[], newPage: number, selectedDriverId?: string, isNavigation: boolean = true) => {
    newPage = newPage;
    setPage(newPage);
    setSelectedItems(selectedDriverId || '');

    const startIndex = (newPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;

    if (selectedDriverId) {

      if (!isNavigation) {
        const selectedDriver = list.find((d) => d.id === selectedDriverId);
        const filteredList = list.filter((d) => d.id !== selectedDriverId);

        const totalItemsLoaded = newPage * PAGE_SIZE;
        const allLoadedItems = filteredList.slice(0, totalItemsLoaded - 1);

        const reorganizedData = [
          selectedDriver,
          ...allLoadedItems
        ].filter(Boolean);
        
        setData(reorganizedData.filter((d): d is Driver => !!d));
      } else {

        if (newPage > 1) {

          const newPageItems = list.slice(startIndex, endIndex).filter(d => d.id !== selectedDriverId);
          setData(prevData => [...prevData, ...newPageItems]);
        } else {

          const selectedDriver = list.find((d) => d.id === selectedDriverId);
          const filteredList = list.filter((d) => d.id !== selectedDriverId);
          const remainingDrivers = filteredList.slice(0, PAGE_SIZE - 1);
          const pageDrivers = [
            selectedDriver,
            ...remainingDrivers
          ].filter(Boolean);
          setData(pageDrivers.filter((d): d is Driver => !!d));
        }
      }
    } else {

      if (newPage > 1 && isNavigation) {
        setData(prevData => [...prevData, ...list.slice(startIndex, endIndex)]);
      } else {
        setData(list.slice(startIndex, endIndex));
      }
    }
  }

  const handlePage = (params: { page: number, pages?: number }) => {
    let newPage = params.page || page;
    newPage = newPage === 0 ? 1 : newPage;
    let list = searchValue && searchValue !== '' ? copyfiltered : allDrivers;

    ManipulatorPage(list, newPage, selectedItems || undefined, true);
  };

  useEffect(() => {
    if (Client?.getActiveClient().token?.accessToken) {
      initialData();
    }
  }, [Client?.getActiveClient().token?.accessToken, amityRioDrivers]);

  useEffect(() => {
    if (error) {
      Notification.error(error);
    }
  }, [error])

  const [content, setContent] = useState(5);

  const pageIndex = 1;
  const onContent = (idx: number) => {
    if (!selectedItems && idx > pageIndex) {
      setError("Selecione o motorista")
      return;
    }

    if (selectedItems)
      setDriver()

    navigate(ProgressOptions[idx]?.route + '?' + currentParams.toString(), { state: { edit: navigateState?.edit, cvs: navigateState?.csv } })
  }

  return (
    <div className="display-flex flex-column gap-20 padding-y-20 padding-x-50">
      <div>
        <Title
          icon="rioglyph rioglyph-user"
          title="Motorista para a viagem"
        />
      </div>
      <div>
        <Progress.Trip
          selected={content}
          onSelectedChanged={onContent}
          disabledIndex={pageIndex + 1}
          step={pageIndex}
        />
      </div>
      <div>
        <TableSearch value={searchValue} onChange={handleSearchName} placeholder="Buscar motorista" />
      </div>
      <div className="padding-25 bg-white rounded shadow-default">
        <div className="margin-bottom-15">
          <span className="text-bold text-size-16">{total} motoristas encontrados:</span>
        </div>
        {data && data.length > 0 ? (
          <ExpanderListDriver
            onChecked={handleChecked}
            selected={selectedItems} //temp remover
            onClickButton={(type, _, driver) => {
              modals[type](driver!);
            }}
            data={data}
          />
        ) : (
          <NotFoundState
            headline="Nenhum motorista disponível"
            message="Volte e tente novamente."
            fullWidth
            outerClassName="border-none"
          />
        )}

        <div className="display-flex justify-content-between align-items-center">
          <div className="display-flex width-200">
          </div>
          <div className="display-flex justify-content-center">
            <Paginator
              handlePage={params => handlePage(params)}
              pages={pages}
              page={page}
              pageSize={PAGE_SIZE}
              disabled={data.length == 0}
            />
          </div>
          <div className="display-flex gap-10">
            <Button bsSize={Button.MD} className="width-84" onClick={handleCancel}>
              Voltar
            </Button>
            <Button disabled={!selectedItems} bsStyle={Button.PRIMARY} bsSize={Button.MD} className="width-157" onClick={handleSubmit} >
              {navigateState?.edit ? "Aplicar" : 'Avançar'}
            </Button>
          </div>
        </div>
        <Dialog.PerfilSocial driver={userInfo} open={openPerfilModal} onClose={handleClosePerfilModal} userProfile={userDetailAmity} />
        <Dialog.DriverVehicle id={userInfo?.id} open={openVehicleModal} onClose={handleCloseVehicleModal} />
      </div>
    </div>
  );
};

export default TripsDriverScreen;

